import { FinancialUnavailable, isCurrentAssessment, type FinancialResponseAvailability, type FinancialNumeric } from '../financial-unavailable';
/**
 * Lender Health Scores Panel (SPRINT-6B F010)
 *
 * Displays lender-focused credit assessment with:
 * - Overall score (X/100)
 * - Health rating badge
 * - Component progress bars (Serviceability, Leverage, Liquidity, Coverage)
 * - Key metrics (ICR, DSCR, LVR)
 * - Concerns and mitigating factors as tag lists
 * - Lender recommendation badge
 * - Calculate button to trigger backend computation
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Calculator, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { cn } from '@/lib/utils';

export interface LenderHealthScoresPanelProps {
  dealId: string;
}

interface LenderHealthScore extends FinancialResponseAvailability {
  id: string;
  dealId: string;
  financialPeriodId: string | null;
  assessmentDate: string | null;
  periodLabel: string | null;
  serviceabilityScore: FinancialNumeric;
  leverageScore: FinancialNumeric;
  liquidityScore: FinancialNumeric;
  coverageScore: FinancialNumeric;
  overallLenderScore: FinancialNumeric;
  healthRating: string | null;
  trendDirection: string | null;
  icrRatio: FinancialNumeric;
  dscrRatio: FinancialNumeric;
  lvr: FinancialNumeric;
  keyConcerns: string[];
  mitigatingFactors: string[];
  lenderRecommendation: string | null;
}

interface CalculateResult extends FinancialResponseAvailability {
  overallLenderScore: FinancialNumeric;
  healthRating: string | null;
  lenderRecommendation: string | null;
  componentScores: {
    serviceability: FinancialNumeric;
    leverage: FinancialNumeric;
    liquidity: FinancialNumeric;
    coverage: FinancialNumeric;
  };
  keyMetrics: {
    icr: FinancialNumeric;
    dscr: FinancialNumeric;
    lvr: FinancialNumeric;
  };
  concerns: string[];
  mitigators: string[];
}

// Health rating display config
const HEALTH_RATING_CONFIG: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive'; className: string }> = {
  investment_grade: { label: 'Investment Grade', variant: 'default', className: 'bg-green-500 hover:bg-green-600 text-white' },
  acceptable: { label: 'Acceptable', variant: 'secondary', className: 'bg-blue-500 hover:bg-blue-600 text-white' },
  watchlist: { label: 'Watchlist', variant: 'outline', className: 'bg-amber-500 hover:bg-amber-600 text-white' },
  substandard: { label: 'Substandard', variant: 'outline', className: 'bg-orange-500 hover:bg-orange-600 text-white' },
  doubtful: { label: 'Doubtful', variant: 'destructive', className: 'bg-red-500 hover:bg-red-600 text-white' },
};

// Recommendation display config
const RECOMMENDATION_CONFIG: Record<string, { label: string; className: string }> = {
  approve: { label: 'Approve', className: 'bg-green-500 hover:bg-green-600 text-white' },
  conditional_approve: { label: 'Conditional Approve', className: 'bg-blue-500 hover:bg-blue-600 text-white' },
  refer: { label: 'Refer', className: 'bg-amber-500 hover:bg-amber-600 text-white' },
  decline: { label: 'Decline', className: 'bg-red-500 hover:bg-red-600 text-white' },
};

// Get progress bar colour based on score
function getScoreColour(value: FinancialNumeric): string {
  const score = value == null ? null : Number(value);
  if (score == null) return 'bg-muted';
  if (score >= 80) return 'bg-green-500';
  if (score >= 65) return 'bg-blue-500';
  if (score >= 50) return 'bg-amber-500';
  if (score >= 35) return 'bg-orange-500';
  return 'bg-red-500';
}

const ScopedLenderHealthScoresPanel: React.FC<LenderHealthScoresPanelProps> = ({ dealId }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [calculatedData, setCalculatedData] = useState<CalculateResult | null>(null);

  // Fetch existing health scores
  const { data: healthScores = [], isLoading, isError } = useQuery<LenderHealthScore[]>({
    queryKey: [`/api/crm/deals/${dealId}/lender-health-scores`],
    enabled: !!dealId,
  });

  // Calculate mutation
  const calculateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', '/api/financial/calculate-health-score', { dealId });
      return res.json() as Promise<CalculateResult>;
    },
    onSuccess: (data) => {
      setCalculatedData(data);
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/lender-health-scores`] });
      toast({
        title: isCurrentAssessment(data) ? 'Health score calculated' : 'Not assessed',
        description: !isCurrentAssessment(data) ? 'Add verified financial data, then calculate.' : `Overall score: ${data.overallLenderScore}/100 (${HEALTH_RATING_CONFIG[data.healthRating ?? ""]?.label || data.healthRating})`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to calculate health score',
        variant: 'destructive',
      });
    },
  });

  // Get latest score from DB or calculated result
  const latestScore = healthScores.length > 0 ? healthScores[healthScores.length - 1] : null;

  // Determine what to display
  const hasData = calculatedData || latestScore;

  // Build display data from calculated result or latest score
  const displayData = calculatedData ? {
    overallLenderScore: calculatedData.overallLenderScore,
    healthRating: calculatedData.healthRating,
    lenderRecommendation: calculatedData.lenderRecommendation,
    serviceabilityScore: calculatedData.componentScores.serviceability,
    leverageScore: calculatedData.componentScores.leverage,
    liquidityScore: calculatedData.componentScores.liquidity,
    coverageScore: calculatedData.componentScores.coverage,
    icrRatio: calculatedData.keyMetrics.icr,
    dscrRatio: calculatedData.keyMetrics.dscr,
    lvr: calculatedData.keyMetrics.lvr,
    keyConcerns: calculatedData.concerns,
    mitigatingFactors: calculatedData.mitigators,
  } : latestScore ? {
    overallLenderScore: latestScore.overallLenderScore,
    healthRating: latestScore.healthRating || '',
    lenderRecommendation: latestScore.lenderRecommendation || '',
    serviceabilityScore: latestScore.serviceabilityScore,
    leverageScore: latestScore.leverageScore,
    liquidityScore: latestScore.liquidityScore,
    coverageScore: latestScore.coverageScore,
    icrRatio: latestScore.icrRatio,
    dscrRatio: latestScore.dscrRatio,
    lvr: latestScore.lvr,
    keyConcerns: latestScore.keyConcerns || [],
    mitigatingFactors: latestScore.mitigatingFactors || [],
  } : null;

  const ratingConfig = displayData ? HEALTH_RATING_CONFIG[displayData.healthRating ?? ""] : null;
  const recConfig = displayData ? RECOMMENDATION_CONFIG[displayData.lenderRecommendation ?? ""] : null;

  return (
    <Card data-testid="lender-health-scores-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold">Lender Assessment</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => calculateMutation.mutate()}
            disabled={calculateMutation.isPending}
          >
            {calculateMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Calculator className="h-4 w-4 mr-1" />
            )}
            Calculate Health Score
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : isError || calculateMutation.isError || !isCurrentAssessment(calculatedData ?? latestScore) ? (<FinancialUnavailable dealId={dealId} value={isError || calculateMutation.isError ? { assessmentStatus: 'failed', historical: latestScore } : calculatedData ?? latestScore} action="Add verified financial data, then calculate." />) : !hasData ? (
          /* No data state */
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-sm text-muted-foreground mb-4">No lender assessment available</p>
            <p className="text-xs text-muted-foreground">
              Click Calculate Health Score to analyse the deal
            </p>
          </div>
        ) : displayData && (
          <div className="space-y-4">
            {/* Overall Score Display */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-3xl font-bold">{displayData.overallLenderScore}/100</div>
                </div>
                {ratingConfig && (
                  <Badge
                    data-testid="health-rating-badge"
                    className={cn('text-xs', ratingConfig.className)}
                  >
                    {ratingConfig.label}
                  </Badge>
                )}
              </div>
              {recConfig && (
                <Badge
                  data-testid="recommendation-badge"
                  className={cn('text-xs', recConfig.className)}
                >
                  {recConfig.label}
                </Badge>
              )}
            </div>

            {/* Component Scores */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-muted-foreground">COMPONENT SCORES</h4>

              {/* Serviceability */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-muted-foreground">Serviceability</span>
                  <span className="text-sm font-medium">{displayData.serviceabilityScore}/100</span>
                </div>
                <div
                  role="progressbar"
                  aria-valuenow={displayData.serviceabilityScore == null ? undefined : Number(displayData.serviceabilityScore)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  className="h-2 w-full rounded-full bg-gray-200 overflow-hidden"
                >
                  <div
                    className={cn('h-full rounded-full transition-all', getScoreColour(displayData.serviceabilityScore))}
                    style={{ width: `${displayData.serviceabilityScore}%` }}
                  />
                </div>
              </div>

              {/* Leverage */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-muted-foreground">Leverage</span>
                  <span className="text-sm font-medium">{displayData.leverageScore}/100</span>
                </div>
                <div
                  role="progressbar"
                  aria-valuenow={displayData.leverageScore == null ? undefined : Number(displayData.leverageScore)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  className="h-2 w-full rounded-full bg-gray-200 overflow-hidden"
                >
                  <div
                    className={cn('h-full rounded-full transition-all', getScoreColour(displayData.leverageScore))}
                    style={{ width: `${displayData.leverageScore}%` }}
                  />
                </div>
              </div>

              {/* Liquidity */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-muted-foreground">Liquidity</span>
                  <span className="text-sm font-medium">{displayData.liquidityScore}/100</span>
                </div>
                <div
                  role="progressbar"
                  aria-valuenow={displayData.liquidityScore == null ? undefined : Number(displayData.liquidityScore)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  className="h-2 w-full rounded-full bg-gray-200 overflow-hidden"
                >
                  <div
                    className={cn('h-full rounded-full transition-all', getScoreColour(displayData.liquidityScore))}
                    style={{ width: `${displayData.liquidityScore}%` }}
                  />
                </div>
              </div>

              {/* Coverage */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-muted-foreground">Coverage</span>
                  <span className="text-sm font-medium">{displayData.coverageScore}/100</span>
                </div>
                <div
                  role="progressbar"
                  aria-valuenow={displayData.coverageScore == null ? undefined : Number(displayData.coverageScore)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  className="h-2 w-full rounded-full bg-gray-200 overflow-hidden"
                >
                  <div
                    className={cn('h-full rounded-full transition-all', getScoreColour(displayData.coverageScore))}
                    style={{ width: `${displayData.coverageScore}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="border-t pt-4">
              <h4 className="text-xs font-semibold text-muted-foreground mb-2">KEY METRICS</h4>
              <dl className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <dt className="text-muted-foreground">ICR</dt>
                  <dd className="font-medium">{displayData.icrRatio != null ? `${Number(displayData.icrRatio).toFixed(2)}x` : 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">DSCR</dt>
                  <dd className="font-medium">{displayData.dscrRatio != null ? `${Number(displayData.dscrRatio).toFixed(2)}x` : 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">LVR</dt>
                  <dd className="font-medium">{displayData.lvr != null ? `${Number(displayData.lvr).toFixed(0)}%` : 'N/A'}</dd>
                </div>
              </dl>
            </div>

            {/* Concerns & Mitigators */}
            {(displayData.keyConcerns.length > 0 || displayData.mitigatingFactors.length > 0) && (
              <div className="border-t pt-4 space-y-3">
                {displayData.keyConcerns.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground mb-2">KEY CONCERNS</h4>
                    <div className="flex flex-wrap gap-1">
                      {displayData.keyConcerns.map((concern, idx) => (
                        <Badge key={idx} variant="destructive" className="text-xs">
                          {concern}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {displayData.mitigatingFactors.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground mb-2">MITIGATING FACTORS</h4>
                    <div className="flex flex-wrap gap-1">
                      {displayData.mitigatingFactors.map((factor, idx) => (
                        <Badge key={idx} variant="default" className="text-xs bg-green-500 hover:bg-green-600 text-white">
                          {factor}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const LenderHealthScoresPanel: React.FC<LenderHealthScoresPanelProps> = props => <ScopedLenderHealthScoresPanel key={props.dealId} {...props} />;

export default LenderHealthScoresPanel;
